import React, {useCallback, useEffect, useState} from "react"
import {useTranslation} from "react-i18next"
import * as Requests from "../../library/requests/Requests"
import Spinner from "../common/Spinner"
import GallerySample from "./GallerySample"
import {useConfig} from "../../hooks/useConfig"
import {UploadResponse} from "../../library/Upload"
import {Participant} from "../../library/Participant"
import {Box, Fade, Stack} from "@mui/material"
import BigButton from "../buttons/BigButton"
import Typography from "@mui/material/Typography"
import {useDelayedSwap} from "../../hooks/useDelayedSwap"

interface WrapUpViewProps {
  participant: Participant
  compositeImage: string
  rawFrame: string
  reset: () => void
  finish: (uploadCode: string) => void
}

function WrapUpView({participant, compositeImage, rawFrame, reset, finish}: WrapUpViewProps) {
  const {t} = useTranslation()
  const config = useConfig()

  const [uploadCode, setUploadCode] = useState<string | undefined>()
  const [uploadShareUrl, setUploadShareUrl] = useState<string | undefined>()
  const [uploadError, setUploadError] = useState<string | undefined>()
  const [showGalleryOptIn, setShowGalleryOptIn] = useState(config.showGalleryOptIn)
  const [isUploading, setIsUploading] = useState(!config.showGalleryOptIn)
  const [isProcessing, setIsProcessing] = useState(false)

  const checkProcessingStatus = useCallback(
    (id: number, code: string) => {
      Requests.checkProcessedStatus(id, (isProcessed: boolean) => {
        if (isProcessed) {
          setIsProcessing(false)
          finish(code)
        } else {
          setTimeout(() => checkProcessingStatus(id, code), 1000)
        }
      })
    },
    [finish],
  )

  const startUpload = useCallback(
    (isPublic: boolean) => {
      setIsUploading(true)
      setShowGalleryOptIn(false)

      Requests.upload(
        config,
        participant.sessionId,
        compositeImage,
        rawFrame,
        isPublic,
        participant ?? null,
        (json: UploadResponse) => {
          if (json.success) {
            setIsUploading(false)
            setIsProcessing(true)
            setUploadCode(json.url)
            setUploadShareUrl(json.shareURL)

            checkProcessingStatus(json.id, json.url)
          } else {
            setIsUploading(false)
            setUploadError(json.message ?? "An error occurred while uploading.")
          }
        },
      )
    },
    [config, participant, compositeImage, rawFrame, checkProcessingStatus],
  )

  useEffect(() => {
    if (!compositeImage) {
      if (uploadCode && uploadShareUrl) {
        window.open(uploadShareUrl)
        reset()
      }
      return
    }

    if (!showGalleryOptIn) {
      const isUploadPublic = config.gallery.privacy !== "private"
      startUpload(isUploadPublic)
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getUploadStatusKey = () => {
    if (isUploading) {
      return "finish.uploading"
    }
    if (isProcessing) {
      return "finish.processing"
    }
    return "finish.finishing"
  }

  const theme = config.theme
  const statusKey = getUploadStatusKey()
  const {displayed: displayedKey, inProp} = useDelayedSwap(statusKey, 500)

  if (showGalleryOptIn) {
    return (
      <Fade in appear timeout={1000}>
        <Box>
          <Stack alignItems="center" spacing={2} sx={{mt: "40px", maxWidth: 720, mx: "auto", color: config.theme.textColor}}>
            <Typography fontSize={32} fontWeight={500}>
              {t("finish.galleryOptIn.prompt", {galleryTitle: config.gallery.title})}
            </Typography>
            <Typography fontSize={16} fontWeight={300} >
              {t("finish.galleryOptIn.explanation")}
            </Typography>
            <Stack direction="row" spacing={3}>
              <BigButton
                variant="outlined"
                sx={{
                  display: "inline-block",
                  width: "auto",
                  px: 4,
                  "@media (max-width: 390px)": { px: "25px" },
                  "@media (max-width: 320px)": { px: "15px" },
                }}
                onClick={() => startUpload(false)}
              >
                {t("finish.galleryOptIn.no")}
              </BigButton>
              <BigButton
                sx={{
                  display: "inline-block",
                  width: "auto",
                  px: 4,
                  position: "relative",
                  top: -3,
                  height: 71,
                  "@media (max-width: 390px)": { px: "25px" },
                  "@media (max-width: 320px)": { px: "15px" },
                }}
                onClick={() => startUpload(true)}
              >
                {t("finish.galleryOptIn.yes")}
              </BigButton>
            </Stack>
          </Stack>
          <Box maxWidth={1024} justifySelf="center">
            <GallerySample gallery={config.gallery}/>
          </Box>
        </Box>
      </Fade>
    )
  }

  return (
    <>
      {uploadError == null && (
        <Box sx={{mt: 3}}>
          <Spinner color={theme.textStyle.color}/>
        </Box>
      )}
      <Fade in={inProp} timeout={500}>
        <Typography variant="h5" sx={{mt: 3, ...theme.textStyle}}>
          {uploadError ? uploadError : t(displayedKey)}
        </Typography>
      </Fade>
    </>
  )
}

export default WrapUpView
